import type { loader } from './Home.data.ts';

import { styled } from '@mend/mui';
import { Box, Tab, Tabs } from '@mui/material';
import { Outlet, useLoaderData, useSearchParams } from 'react-router';

import { ActiveProviders } from '#routes/Home/Widgets/ActiveProviders.tsx';
import { FormCompletionJourney } from '#routes/Home/Widgets/FormCompletionJourney.tsx';
import { FormCompletionTimeTrends } from '#routes/Home/Widgets/FormCompletionTimeTrends.tsx';
import { Address } from './Filters/Address.tsx';
import { AppointmentTypes } from './Filters/AppointmentTypes.tsx';
import { AssessmentType } from './Filters/AssessmentType.tsx';
import { BookingChannel } from './Filters/BookingChannel.tsx';
import { DateRange } from './Filters/DateRange.tsx';
import { FormPriority } from './Filters/FormPriority.tsx';
import { Provider } from './Filters/Provider.tsx';
import { AggregatedAppointmentsByType } from './Widgets/AggregatedAppointmentsByType.tsx';
import { AppointmentRates } from './Widgets/AppointmentRates.tsx';
import { AppointmentsByType } from './Widgets/AppointmentsByType.tsx';
import { FormCompletionByType } from './Widgets/FormCompletionByType.tsx';
import { FormTrends } from './Widgets/FormTrends/FormTrends.tsx';
import { FormTypeBreakdown } from './Widgets/FormTypeBreakdown.tsx';
import { ActiveProvidersRate } from './Widgets/Stats/ActiveProvidersRate.tsx';
import { FormCompletionTime } from './Widgets/Stats/FormCompletionTime.tsx';
import { FormsAssigned } from './Widgets/Stats/FormsAssigned.tsx';
import { FormsCompleted } from './Widgets/Stats/FormsCompleted.tsx';
import { FormsCompletionRate } from './Widgets/Stats/FormsCompletionRate.tsx';
import { NoShowRate } from './Widgets/Stats/NoShowRate.tsx';
import { TotalAppointments } from './Widgets/Stats/TotalAppointments.tsx';
import { TotalPatientsSeen } from './Widgets/Stats/TotalPatientsSeen.tsx';

const Grid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

const StatWidgetGrid = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

const FiltersGrid = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

export function Home() {
  const data = useLoaderData<ReturnType<typeof loader>>();

  const [, setSearchParams] = useSearchParams();

  return (
    <>
      <Box mb={4}>
        <Tabs
          value={data.tabIndex}
          onChange={(_event, value) => {
            setSearchParams((searchParams) => {
              searchParams.set('tab', value === 0 ? 'scheduling' : 'forms');
              return searchParams;
            });
          }}
          aria-label="Section tabs"
        >
          <Tab label="Scheduling" {...getRouteTabProps(0)} />
          <Tab label="Forms" {...getRouteTabProps(1)} />
        </Tabs>
      </Box>

      <FiltersGrid>
        <DateRange defaultValue={[data.from, data.to]} />
        <Provider defaultValue={data.providerIds} />
        {data.tab === 'scheduling' ? (
          <>
            <Address defaultValue={data.addressIds} />
            <BookingChannel defaultValue={data.bookingChannelIds} />
            <AppointmentTypes
              defaultValue={{
                baseAppointmentTypes: data.baseAppointmentTypes,
                appointmentTypeIds: data.appointmentTypeIds,
              }}
            />
          </>
        ) : (
          <>
            <AssessmentType defaultValue={data.assessmentTypeIds} />
            <FormPriority defaultValue={data.formsPriority} />
          </>
        )}
      </FiltersGrid>

      <TabPanel index={0} value={data.tabIndex}>
        <StatWidgetGrid>
          <ActiveProvidersRate />
          <TotalPatientsSeen />
          <TotalAppointments />
          <NoShowRate />
        </StatWidgetGrid>

        <Grid>
          <AppointmentRates />
          <AppointmentsByType />
          <AggregatedAppointmentsByType />
          <ActiveProviders />
        </Grid>
      </TabPanel>

      <TabPanel index={1} value={data.tabIndex}>
        <StatWidgetGrid>
          <FormsAssigned />
          <FormsCompleted />
          <FormsCompletionRate />
          <FormCompletionTime />
        </StatWidgetGrid>

        <Grid>
          <FormTrends />
          <FormCompletionJourney />
          <FormTypeBreakdown />
          <FormCompletionByType />
          <FormCompletionTimeTrends />
        </Grid>
      </TabPanel>

      {/* Render the route dialogs that can be opened from the widgets */}
      <Outlet />
    </>
  );
}

function getRouteTabProps(index: number) {
  return {
    id: `sections-tab-${index}`,
    'aria-controls': `sections-tabpanel-${index}`,
  };
}

/**
 * TODO: Once the activity component is stable, we should test it here so that
 * we can render the children of all tab panels and potentially improve
 * performance.
 *
 * @see https://react.dev/reference/react/Activity
 */
function TabPanel(props: {
  index: number;
  value: number;
  children: React.ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      hidden={props.value !== props.index}
      id={`sections-tabpanel-${props.index}`}
      aria-labelledby={`sections-tab-${props.index}`}
    >
      {props.value === props.index ? props.children : null}
    </div>
  );
}
